import { useEffect, useRef, useCallback } from 'react';

const STORAGE_KEY = 'indikin_exit_intent_ts';
const SESSION_KEY = 'indikin_exit_intent_session';
// [v2 FIX] Reduced from 30 to 14 days per conversion psychology expert
const SUPPRESSION_DAYS = 14;
const CTA_SUPPRESSION_DAYS = 7;
const ARM_DELAY_MS = 10000;

/**
 * useExitIntent - Detects user exit intent via mouse trajectory and tab visibility.
 *
 * @param {Object} options
 * @param {boolean} options.enabled - Master enable flag (all eligibility conditions met)
 * @param {Function} options.onTrigger - Callback when exit intent is detected
 */
export function useExitIntent({ enabled, onTrigger }) {
  const armed = useRef(false);
  const triggered = useRef(false);

  // Arm the trigger after a delay
  // [v2 FIX] Reset armed.current when enabled toggles off
  useEffect(() => {
    if (!enabled) {
      armed.current = false;
      return;
    }
    const timer = setTimeout(() => {
      armed.current = true;
    }, ARM_DELAY_MS);
    return () => clearTimeout(timer);
  }, [enabled]);

  const fire = useCallback(() => {
    if (!armed.current || triggered.current) return;
    triggered.current = true;
    // [v2 FIX] Persist session-shown flag in sessionStorage (survives HMR/strict mode)
    try {
      sessionStorage.setItem(SESSION_KEY, 'true');
    } catch { /* noop */ }
    onTrigger();
  }, [onTrigger]);

  // Desktop: Mouse exits viewport upward
  // [v2 FIX] Use 'mouseleave' (does NOT bubble) instead of 'mouseout' (bubbles, fires hundreds of times)
  useEffect(() => {
    if (!enabled) return;
    const handler = (e) => {
      if (e.clientY <= 0) {
        fire();
      }
    };
    document.documentElement.addEventListener('mouseleave', handler);
    return () => document.documentElement.removeEventListener('mouseleave', handler);
  }, [enabled, fire]);

  // Tab switch: Show popup when user returns
  useEffect(() => {
    if (!enabled) return;
    let leftTab = false;
    const handler = () => {
      if (!armed.current || triggered.current) return;
      if (document.visibilityState === 'hidden') {
        leftTab = true;
      } else if (document.visibilityState === 'visible' && leftTab) {
        leftTab = false;
        fire();
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [enabled, fire]);
}

/**
 * Check if the popup is suppressed by a previous dismissal.
 * [v2 FIX] consent parameter gates localStorage reads for ePrivacy compliance.
 * @param {string|null} consent - Cookie consent state ('accepted', 'declined', or null)
 */
export function isExitIntentSuppressed(consent) {
  // [v2 FIX] Check session flag first (in-memory alternative, always readable)
  try {
    if (sessionStorage.getItem(SESSION_KEY) === 'true') return true;
  } catch { /* noop */ }

  // Only read localStorage if user has consented
  if (consent !== 'accepted') return false;

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const { ts, type } = JSON.parse(raw);
    const days = type === 'cta' ? CTA_SUPPRESSION_DAYS : SUPPRESSION_DAYS;
    const elapsed = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return elapsed < days;
  } catch {
    return false;
  }
}

/**
 * Record a dismissal (type: 'dismiss' | 'cta').
 * [v2 FIX] consent parameter gates localStorage writes for ePrivacy compliance.
 * @param {string} type - 'dismiss' or 'cta'
 * @param {string|null} consent - Cookie consent state
 */
export function recordExitIntentDismissal(type = 'dismiss', consent = null) {
  // Always record in sessionStorage (prevents re-show in same session)
  try {
    sessionStorage.setItem(SESSION_KEY, 'true');
  } catch { /* noop */ }

  // [v2 FIX] Only write to localStorage if consent === 'accepted'
  if (consent === 'accepted') {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ts: Date.now(), type }));
    } catch {
      // localStorage unavailable (private browsing, full quota)
    }
  }
}
